import React, { startTransition, useEffect, useMemo } from 'react';
import { NavigateFunction, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  HiOutlineHome,
  HiOutlineChartBar,
  HiOutlinePhotograph,
  HiOutlinePresentationChartLine,
  HiOutlineCog,
  HiOutlineMenuAlt2,
  HiOutlineUserGroup,
  HiOutlineSpeakerphone,
  HiOutlineCalendar,
  HiOutlineHashtag,
  HiOutlineScale,
  HiOutlineSparkles,
  HiOutlineTag,
  HiOutlineBeaker,
} from 'react-icons/hi';
import { MdOutlineAnalytics } from 'react-icons/md';
import { ROUTES } from '@/routes';
import { RootState } from '@/store';
import type { User } from '@/types/auth.types';
import { IconType } from 'react-icons';
import clsx from 'clsx';
import { shouldDelegateNavToBrowser } from '@/utils/navClick';
import { useNewUntilVisited } from '@/hooks/useNewUntilVisited';
import Icon from './Icon';
import Badge, { BADGE_ON_TOP } from './ui/Badge';
import Logo from '@/assets/Logo_letras_blancas.png';

interface SidebarProps {
  onToggle: () => void;
}

export interface NavItem {
  name: string;
  icon: IconType;
  href: string;
  roles?: User['role'][];
  newUntilVisitedKey?: string;
}

const navigation: NavItem[] = [
  { name: 'Agent', icon: HiOutlineHome, href: ROUTES.HOME },
  { name: 'Social Resonance', icon: HiOutlineChartBar, href: ROUTES.SOCIAL_RESONANCE },
  { name: 'Creative', icon: HiOutlinePhotograph, href: ROUTES.CREATIVE },
  { name: 'Media', icon: HiOutlinePresentationChartLine, href: ROUTES.MEDIA_METRICS },
  { name: 'AI Measurement', icon: MdOutlineAnalytics, href: ROUTES.AI_MEASUREMENT },
  { name: 'Top 5 content', icon: HiOutlineSparkles, href: ROUTES.TOP_CONTENT },
  { name: 'Hashtag campaigns', icon: HiOutlineHashtag, href: ROUTES.HASHTAG_CAMPAIGNS },
  { name: 'Creators', icon: HiOutlineUserGroup, href: ROUTES.CREATORS },
  { name: 'Comparison', icon: HiOutlineScale, href: ROUTES.INFLUENCER_COMPARISON },
  { name: 'Listening', icon: HiOutlineSpeakerphone, href: ROUTES.LISTENING },
  { name: 'Competitor ads', icon: HiOutlineTag, href: ROUTES.ADS },
  {
    name: 'Pre-testing',
    icon: HiOutlineBeaker,
    href: ROUTES.PRETEST,
    newUntilVisitedKey: 'pretest-nav-seen',
  },
  {
    name: 'Schedule',
    icon: HiOutlineCalendar,
    href: ROUTES.SCHEDULE,
    roles: ['ADMIN', 'SUPER_USER'],
  },
];

interface SidebarNavLinkProps {
  item: NavItem;
  navigate: NavigateFunction;
  userId?: string;
}

export const SidebarNavLink: React.FC<SidebarNavLinkProps> = ({ item, navigate, userId }) => {
  const { pathname } = useLocation();
  const onRoute = pathname === item.href || pathname.startsWith(`${item.href}/`);
  const { isNew, markVisited } = useNewUntilVisited(
    item.newUntilVisitedKey && userId ? `${item.newUntilVisitedKey}:${userId}` : null
  );

  useEffect(() => {
    if (onRoute) markVisited();
  }, [onRoute, markVisited]);

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    markVisited();
    if (shouldDelegateNavToBrowser(e)) return;
    e.preventDefault();
    startTransition(() => navigate(item.href));
  };

  return (
    <NavLink
      to={item.href}
      end={item.href === ROUTES.HOME}
      onClick={onClick}
      className={({ isActive }) =>
        clsx(
          'relative flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors',
          isActive
            ? 'bg-green-500/20 text-white'
            : 'text-gray-300 hover:bg-dark-700 hover:text-white'
        )
      }
    >
      <Icon icon={item.icon} className="w-5 h-5 mr-3" />
      {item.name}
      {isNew && <Badge className={BADGE_ON_TOP}>New</Badge>}
    </NavLink>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ onToggle }) => {
  const navigate = useNavigate();
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const userId = useSelector((state: RootState) => state.auth.user?.id);

  const visibleNav = useMemo(() => {
    return navigation.filter(item => {
      if (!item.roles?.length) return true;
      return role != null && item.roles.includes(role);
    });
  }, [role]);

  return (
    <div className="flex flex-col w-64 h-full bg-primary-900 border-r border-primary-900">
      <div className="flex items-center justify-between h-16 px-4 border-b border-dark-700">
        <NavLink to={ROUTES.HOME} className="flex items-center pl-3 py-2">
          <img src={Logo} alt="logo" translate="no" className="h-10" />
        </NavLink>
        <button
          onClick={onToggle}
          className="text-gray-400 hover:text-white transition-all duration-200 p-2 hover:scale-110"
        >
          <Icon icon={HiOutlineMenuAlt2} className="w-6 h-6" />
        </button>
      </div>

      <nav className="flex-1 px-4 py-6 space-y-2">
        {visibleNav.map(item => (
          <SidebarNavLink key={item.name} item={item} navigate={navigate} userId={userId} />
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-dark-700">
        <button className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-300 rounded-lg hover:bg-dark-700 hover:text-white transition-colors">
          <Icon icon={HiOutlineCog} className="w-5 h-5 mr-3" />
          Settings
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
